/**
 * A screen that could not be fetched because a deploy replaced it: reload once.
 *
 * Found walking the deployed build, 2 October. The coach's screens are loaded
 * lazily, from files whose names change with every build. A page opened before
 * a deploy still asks for the old names, the deploy has removed them, and the
 * coach tapping Availability was shown "Importing a module script failed."
 * Reloading fetches the page that knows the new names.
 *
 * Nothing worth keeping is lost. The cart is the server's, and its clock counts
 * to the server's expires_at, so a reload neither empties it nor restarts it.
 * Sign-in is in sessionStorage, which survives a reload of the same tab. What a
 * reload does lose is whatever was typed and not yet saved — and this only fires
 * as somebody opens a screen, which is when they have just left one.
 *
 * ONCE, NOT IN A LOOP. If the file is missing for some other reason — the site
 * down, the network gone — a reload fails the same way, and reloading again
 * would spin. A second failure within the window is let through to the error the
 * screen already shows. If storage cannot be read or written, the reload is not
 * attempted at all: without a record of the last one, nothing stops the loop.
 */

const KEY = 'coachtools.reloaded-after-deploy';

/** Long enough to cover the reload and the screen fetch after it. */
const WINDOW_MS = 60_000;

export function reloadAfterDeploy(): void {
  window.addEventListener('vite:preloadError', () => {
    try {
      const last = Number(sessionStorage.getItem(KEY) ?? '0');
      if (Date.now() - last < WINDOW_MS) return;
      sessionStorage.setItem(KEY, String(Date.now()));
    } catch {
      return;
    }
    window.location.reload();
  });
}
